(() => {
  const pending = new Map();
  const normalize = value => String(value ?? '').trim().toUpperCase();
  const tokens = value => (Array.isArray(value) ? value : String(value ?? '').split(/[;,|]/u)).map(normalize).filter(Boolean);
  function watts(raw) {
    let value = String(raw ?? '').trim();
    if (!value) return null;
    const multiplier = /kW$/iu.test(value) ? 1000 : 1;
    value = value.replace(/\s*(?:kW|W|Watt)$/iu, '').trim();
    if (/^\d{1,3}(?:\.\d{3})+(?:,\d+)?$/u.test(value)) value = value.replaceAll('.', '');
    if (!/^\d+(?:[.,]\d+)?$/u.test(value)) return null;
    const number = Number(value.replace(',', '.')) * multiplier;
    return Number.isFinite(number) && number > 0 ? number : null;
  }
  function matchesRating(product, query) {
    const application = normalize(query.get('k4y_temperature'));
    const requested = watts(query.get('k4y_power'));
    if (!requested || !['NK', 'TK'].includes(application)) return false;
    const rating = watts(application === 'NK' ? product.nk : product.tk);
    if (!rating || rating < requested * 0.85 || rating > requested * 1.15) return false;
    const declared = tokens(product.application).flatMap(value => value.split(/[\s/]+/u));
    if (declared.length && !declared.includes(application)) return false;
    const refrigerants = tokens(query.get('f.kaeltemittel'));
    return !refrigerants.length || refrigerants.some(value => tokens(product.rating_refrigerant).includes(value));
  }
  function matchesFilters(product, query, fields) {
    for (const field of fields) {
      const selected = query.get(`f.${field.key}`);
      if (!selected) continue;
      if (field.key === 'preis') {
        const [min, max] = selected.split(':').map(Number);
        if (!Number.isFinite(min) || !Number.isFinite(max) || product.price_min > max || product.price_max < min) return false;
      } else if (field.key === 'hersteller') {
        if (!tokens(selected).includes(normalize(product.vendor))) return false;
      } else if (field.type === 'number') {
        const [min, max] = selected.split(':').map(Number);
        const raw = String(product.fields[field.key] ?? '').trim();
        const values = raw.match(/\d+(?:[.,]\d+)?/gu)?.map(value => Number(value.replace(',', '.'))) || [];
        if (!values.length || !Number.isFinite(min) || !Number.isFinite(max)) return false;
        const lower = Math.min(...values), upper = Math.max(...values);
        if (lower > max || upper < min) return false;
      } else if (field.key !== 'kaeltemittel') {
        if (!tokens(selected).some(value => tokens(product.fields[field.key]).includes(value))) return false;
      }
    }
    return true;
  }
  async function load(collection, source) {
    const preview = source.searchParams.get('preview_theme_id') || '';
    const cacheKey = `${collection}:${preview}`;
    if (pending.has(cacheKey)) return pending.get(cacheKey);
    const request = (async () => {
      async function page(number) {
        const url = new URL(source.pathname, source.origin);
        url.searchParams.set('section_id', 'k4y-selection-data');
        url.searchParams.set('page', String(number));
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('selection-unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const node = doc.querySelector('[data-k4y-selection-data]');
        if (!node) throw new Error('selection-data-missing');
        const data = JSON.parse(node.textContent);
        if (data.page !== number || !Array.isArray(data.products)) throw new Error('selection-page-invalid');
        return data;
      }
      const first = await page(1);
      if (first.pages > 40) throw new Error('selection-too-large');
      const all = [...first.products];
      for (let start = 2; start <= first.pages; start += 3) {
        const pages = await Promise.all(Array.from({ length: Math.min(3, first.pages - start + 1) }, (_, index) => page(start + index)));
        for (const result of pages) all.push(...result.products);
      }
      const unique = [...new Map(all.map(product => [product.handle, product])).values()];
      if (unique.length !== first.total) throw new Error('selection-incomplete');
      return unique;
    })();
    pending.set(cacheKey, request);
    try { return await request; } catch (error) { pending.delete(cacheKey); throw error; }
  }
  function apply(products, query, data) {
    const rated = products.filter(product => matchesRating(product, query));
    const matches = rated.filter(product => matchesFilters(product, query, data.filters));
    const sort = query.get('sort_by');
    if (sort === 'price-ascending') matches.sort((a, b) => a.price_min - b.price_min);
    if (sort === 'price-descending') matches.sort((a, b) => b.price_min - a.price_min);
    if (sort === 'title-ascending') matches.sort((a, b) => a.title.localeCompare(b.title, 'de'));
    if (sort === 'title-descending') matches.sort((a, b) => b.title.localeCompare(a.title, 'de'));
    const page = Math.max(1, Math.min(Number(query.get('page')) || 1, Math.max(1, Math.ceil(matches.length / 24))));
    return { ...data, page, total: matches.length, products: matches.slice((page - 1) * 24, page * 24), ratingTotal: rated.length };
  }
  window.K4YSelection = { load, apply, matchesRating, matchesFilters, watts };
})();
